import {useInfiniteQuery, useQueryClient} from "@tanstack/react-query";
import {useCallback, useContext, useEffect, useState} from 'react';
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {NotificationAlert, NotificationDto} from "@/api/dto/notification";
import {useNotificationStore} from "@/store/notification_store";
import {useAlertStore} from "@/store/alert_store";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {GENERICERROR} from "@manual_encounters/constants/BannerMessages";
import Helpers from "@/helpers/helpers";
import {log} from "@/helpers/logger";
import {useRosterStore} from "@/store/roster_store";

type Options = {
  onlyShowUnreadNotifications: boolean;
  onlyUpdateCount?: boolean;
  enabled?: boolean;
};

export function useNotifications({ onlyShowUnreadNotifications, onlyUpdateCount, enabled = true }: Options) {
  const {
    notifications,
    setNotifications,
    totalNotifications,
    setTotalNotifications,
    totalUnreadNotifications,
    setTotalUnreadNotifications,
    isApiLoading,
    setApiLoading
  } = useNotificationStore();
  const queryClient = useQueryClient();
  const [isNotificationUpdateLoading, setNotificationUpdateLoading] = useState(false);
  const { carecoApi } = useContext(CarecoApiContext);
  const addAlert = useAlertStore((state) => state.addAlert);
  const {currentRoster} = useRosterStore();

  const result = useInfiniteQuery({
    queryKey: ['notifications', currentRoster?.id ?? 'all', onlyShowUnreadNotifications],
    queryFn: ({ pageParam }) => {
      return carecoApi?.getNotifications(onlyShowUnreadNotifications, pageParam, currentRoster?.id);
    },
    enabled: enabled && !!carecoApi,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    initialPageParam: 1,
    refetchInterval: 180_000,
  });

  const {
    data: notificationsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isRefetching,
    isFetched,
    error,
  } = result;

  const getNotificationsData = useCallback((notificationsData: any) => {
    const uniqueIds = new Set();
    return notificationsData.pages.flatMap(
      (page: { data: { notifications: NotificationDto[]; }; }) =>
        page.data.notifications).filter((_: {id: number}) => !uniqueIds.has(_.id) && uniqueIds.add(_.id));
  }, []);

  const getTotalElements = useCallback((notificationsData: any) => {
    return notificationsData.pages[0].data.totalElements;
  }, []);

  // Mark all notifications viewed across all accessible rosters
  const markAllAsRead = () => {
    setNotificationUpdateLoading(true);
    carecoApi?.putAllNotificationsAsViewed()
      .then(() => queryClient.invalidateQueries({queryKey: ['notifications'], exact: false}))
      .then(() => {
        addAlert({
          type: ToastType.SUCCESS,
          content: NotificationAlert.ALL_NOTIFICATIONS_MARKED_AS_READ,
        });
        void queryClient.invalidateQueries({ queryKey: ['notifications'], exact: false });
      })
      .catch(error => {
        addAlert({
          type: ToastType.ERROR,
          content: `${NotificationAlert.FAILED_TO_MARK_ALL_NOTIFICATIONS} ${GENERICERROR} ${Helpers.traceId(error)}`,
        });
        log('error', 'Error updating all notifications viewed', error);
      })
      .finally(() => {
        setNotificationUpdateLoading(false);
      });
  };

  useEffect(() => {
    if (notificationsData) {
      if (!onlyUpdateCount) {
        setNotifications(getNotificationsData(notificationsData));
        setTotalNotifications(getTotalElements(notificationsData));
      }
      if (onlyShowUnreadNotifications) {
        setTotalUnreadNotifications(getTotalElements(notificationsData));
      }
    }
  }, [notificationsData, getNotificationsData, getTotalElements, onlyUpdateCount, onlyShowUnreadNotifications]);

  useEffect(() => {
    setApiLoading(isLoading || isNotificationUpdateLoading);
  }, [isLoading, isNotificationUpdateLoading]);

  return {
    isLoading,
    isApiLoading,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isRefetching,
    isFetched,
    error,
    notifications,
    setNotifications,
    totalNotifications,
    totalUnreadNotifications,
    setTotalUnreadNotifications,
    markAllAsRead,
  };
}
